/**
 * Countdown Block
 *
 * Custom element powering `blocks/countdown.liquid`. Renders a zero-padded
 * days/hours/minutes/seconds countdown and, when the countdown ends, removes
 * both its own DOM and any elements matched by `hide_selectors`.
 *
 * - Timezone handling mirrors the legacy <countdown-timer>: the browser clock
 *   is shifted by `timezone-offset` hours before comparing against start/end.
 * - Selector parsing mirrors consent-gate: comma/newline separated, trimmed,
 *   invalid selectors are ignored instead of throwing.
 * - No MutationObserver: header content is static, targets exist at load.
 */
(function () {
  'use strict';

  /**
   * Split a selector string on commas/newlines into a clean list.
   * @param {string} raw
   * @returns {string[]}
   */
  function parseSelectors(raw) {
    if (!raw) return [];
    return raw
      .split(/[\n,]+/)
      .map(function (selector) {
        return selector.trim();
      })
      .filter(function (selector) {
        return selector.length > 0;
      });
  }

  /**
   * Remove every element matched by the given selectors.
   * Invalid selectors are skipped without throwing.
   * @param {string[]} selectors
   */
  function removeTargets(selectors) {
    selectors.forEach(function (selector) {
      var nodes;
      try {
        nodes = document.querySelectorAll(selector);
      } catch (error) {
        return;
      }
      nodes.forEach(function (node) {
        node.remove();
      });
    });
  }

  /**
   * Inject hide CSS into document.head before DOM removal so targets disappear
   * on the same turn (avoids a paint between style hide and node.remove).
   * @param {string} styleId
   * @param {string} selfId
   * @param {string[]} selectors
   */
  function injectExpireStyles(styleId, selfId, selectors) {
    if (!styleId || document.getElementById(styleId)) return;
    var css = selfId ? '#' + selfId + '{display:none!important}' : '';
    selectors.forEach(function (selector) {
      css += selector + '{display:none!important}';
    });
    if (!css) return;
    var style = document.createElement('style');
    style.id = styleId;
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  }

  /**
   * Zero-pad a non-negative integer to at least two digits.
   * @param {number} value
   * @returns {string}
   */
  function pad2(value) {
    return value < 10 ? '0' + value : String(value);
  }

  /**
   * Write a zero-padded unit value.
   * @param {Element|null} element
   * @param {number} value
   */
  function setUnitValue(element, value) {
    if (!element) return;
    element.textContent = pad2(value);
  }

  var CountdownBlock = class extends HTMLElement {
    constructor() {
      super();
      this.interval = null;
    }

    connectedCallback() {
      this.startTime = new Date(this.getAttribute('start-time'));
      this.endTime = new Date(this.getAttribute('end-time'));
      this.timezoneOffset = parseFloat(this.getAttribute('timezone-offset')) || 0;
      this.hideSelectors = parseSelectors(this.getAttribute('data-hide-selectors'));

      this.daysElement = this.querySelector('[data-countdown-unit="days"]');
      this.hoursElement = this.querySelector('[data-countdown-unit="hours"]');
      this.minutesElement = this.querySelector('[data-countdown-unit="minutes"]');
      this.secondsElement = this.querySelector('[data-countdown-unit="seconds"]');

      // Invalid configuration: hide self, do nothing else.
      if (isNaN(this.startTime.getTime()) || isNaN(this.endTime.getTime())) {
        this.style.display = 'none';
        return;
      }

      this.updateCountdown();
      this.interval = setInterval(() => this.updateCountdown(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    /**
     * End the countdown: hide via head CSS first, then remove targets and self.
     */
    finish() {
      clearInterval(this.interval);
      var styleId = this.id ? this.id.replace('countdown-block-', 'countdown-expire-') : '';
      injectExpireStyles(styleId, this.id, this.hideSelectors);
      removeTargets(this.hideSelectors);
      this.remove();
    }

    updateCountdown() {
      var now = new Date();
      var nowInTargetTZ = new Date(now.getTime() + this.timezoneOffset * 60 * 60 * 1000);

      // Before the window opens: keep the countdown hidden (do not remove).
      if (nowInTargetTZ < this.startTime) {
        this.style.display = 'none';
        return;
      }
      this.style.display = '';

      var timeLeft = this.endTime - nowInTargetTZ;
      if (timeLeft <= 0) {
        this.finish();
        return;
      }

      var days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      var hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      var minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      var seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      setUnitValue(this.daysElement, days);
      setUnitValue(this.hoursElement, hours);
      setUnitValue(this.minutesElement, minutes);
      setUnitValue(this.secondsElement, seconds);
    }
  };

  if (!window.customElements.get('countdown-block')) {
    window.customElements.define('countdown-block', CountdownBlock);
  }
})();
